import { AdServiceWrapper } from '@/services/wrapper/adServiceWrapper'
import { AdServiceHelper } from '@/services/helper/adServiceHelper'
import { Helper } from '@/services/helper/helper'
import Logger from '@/services/helper/Logger'
import ContentWidthHelper from '@/services/helper/contentWidthHelper'
import { CONSTRAINTS } from '@/services/helper/Constraints'
import store from '@/store/AppStore'
import 'whatwg-fetch'

/**
 * AdHandler to handle the ads for home and logout
 */
export class AdHandler {
  constructor (_this) {
    window.AdService = window.AdService || []
    window.logout = window.PageConfig.site === 'll'
    this.fallback = false
    this.sticky = false
    this.dynamicMiddle = false
    this.dynamicSides = false
    this.topName = 'top'
    this.middleName = 'middle'
    this.rightName = 'right'
    this.leftName = 'left'
    this.wallName = 'wall'
    this.urlParams = new URLSearchParams(window.location.search)
    this.adServiceScriptsAdded = false
    this.browserClean = store.state.adServiceParams.deviceclient === 'browser_clean'
    this.showTop = false
    this.showMiddle = false
    this.showLeft = false
    this.showRight = false
    this.showWall = false
    this.isAdBlocker = false
    this.contentWidthHelper = {}
  }

  /**
   * setter for selectedAd
   */
  set selectedAd (ad) {
    store.commit('setSelectedAd', ad)
  }

  /**
   * getter for selectedAd
   */
  get selectedAd () {
    return store.state.selectedAd
  }

  /**
   * sets the values for the template to show/create the needed containers
   */
  setAdContainers () {
    const setPageReadyOnColCountChange = () => {
      store.commit('setPageReady', true)
      document.removeEventListener('colCountChanged', setPageReadyOnColCountChange)
    }
    document.addEventListener('colCountChanged', setPageReadyOnColCountChange)

    this.showTop = ['wallpaper', 'homeframe', 'cinematic-wall'].indexOf(this.selectedAd) > -1
    this.showMiddle = ['billboard', 'cinematic', 'flagship', 'bridge', 'maxiad', 'lounge', 'video-scroll'].indexOf(this.selectedAd) > -1
    this.showLeft = ['double-sitebar', 'bridge', 'homeframe', 'cinematic-wall', 'video-scroll'].indexOf(this.selectedAd) > -1
    this.showRight = ['double-sitebar', 'sitebar', 'flagship', 'bridge', 'wallpaper', 'homeframe', 'cinematic-wall', 'video-scroll'].indexOf(this.selectedAd) > -1
    this.showWall = ['cinematic-wall', 'video-scroll'].indexOf(this.selectedAd) > -1
  }

  /**
   * creates the script tags for the adservice and the connector
   */
  getAdServiceScripts () {
    const _this = this

    return new Promise(async (resolve) => {
      if (!_this.adServiceScriptsAdded) {
        _this.adServiceScriptsAdded = true
        const minified = !window.PageConfig.adConnectorUrl.includes('dev') ? '.min' : ''
        const serviceJsPath = `${window.PageConfig.adConnectorUrl}/service${minified}.js`
        await Helper.addScript(serviceJsPath)

        if (!window.logout) {
          _this.selectedAd = store.state.adData.type

          _this.onAdTypeAvailable()
          _this.setAdContainers()
        }

        resolve()
      }
    })
  }

  /**
   * is being called after we know the ad type (on home we know it right away because the backend give it to us via initialData, on logout we have to ask the adservice for the ad and ad type)
   */
  onAdTypeAvailable () {
    if (!window.logout) {
      this.initializeAdService()
    }

    this.initializeContentWidthHelper()
    this.setAdProperties()
    this.getAds()
  }

  /**
   * creates the "file global" instance of the contentWidthHelper
   * this can only be done with an ad type, because the content gets calculated based on the ad type
   */
  initializeContentWidthHelper () {
    this.contentWidthHelper = new ContentWidthHelper(this.selectedAd)

    if (window.PageConfig.stage.indexOf('live') > -1 || (!window.PageConfig.stage.indexOf('live') > -1 && this.urlParams.get('colcalc') !== 'false')) {
      this.contentWidthHelper.checkResponsiveGrid()

      window.addEventListener('resize', this.contentWidthHelper.setColCase.bind(this.contentWidthHelper)) // this.contentWidthHelper to have to right context
      this.contentWidthHelper.setColCase()
    } else {
      console.log('col calculation off')
    }
  }

  /**
   * here are some props are being set based on the ad type
   */
  setAdProperties () {
    const response = store.state.adData || {}

    if (response.right?.width) {
      this.contentWidthHelper.superbannerSitebarMinWidth = response.right.width
    }

    if (response.variant === 'sticky' || this.showRight) {
      this.sticky = true
    }

    if (response.type === 'wallpaper' || (response.type === 'billboard' && response.variant !== 'dynamic')) {
      this.contentWidthHelper.minCols = 3
    } else if (response.adsize && response.adsize !== '1x1') {
      this.contentWidthHelper.minCols = this.contentWidthHelper.getMinColsByAdWidth(response.width) || this.contentWidthHelper.minCols
    }

    if (window.logout) {
      window.AdService.setParam({ categorytype: `${this.selectedAd}_${this.contentWidthHelper.colCount}` })
    }

    this.contentWidthHelper.setColCase()
  }

  /**
   * for home the adBlocker detection is being started here
   * for logout we have done the detection already with the adService initialization so we go strait to the point after the detection where the ads are being added to the page / created
   */
  async getAds () {
    if (window.logout) {
      this.callAfterTrackingBlockerDetected(true)
    } else {
      const isAdBlocker = await AdServiceWrapper.checkAdBlocker()
      this.callAfterAdBlockerDetection(isAdBlocker)
    }
  }

  /**
   * initializes the adservice
   * it also checks for logout if browserClean is present from the p parameter -> if present we know there is a tracking blocker present
   * if there is no browserClean we have to detect the blockers on our own
   */
  async initializeAdService () {
    // if browserclean isn't present from p parameter we have to check for blockers
    if (this.browserClean) {
      // no detection needed later in getAds BUT we have to switch the adServer before the prio ad call
      AdServiceWrapper.setTrackingBlockerAdServerParams()

      Helper.fireEvent('bdDone', {
        abd: false
      })
    } else if (window.logout) {
      await AdServiceHelper.adServiceBlockDetectionDone()
      this.isAdBlocker = await AdServiceWrapper.checkAdBlocker()

      if (!this.isAdBlocker) {
        const isTrackingBlocker = AdServiceWrapper.checkTrackingBlocker()

        if (isTrackingBlocker) {
          this.browserClean = true
        }
      }
    }

    // setting a different adServer results in resetting all the set params, so we do the init after the blocking detection
    AdServiceWrapper.init('service', undefined, this.contentWidthHelper.colCount)

    AdServiceHelper.listenEvent('initialize.end', () => {
      AdServiceWrapper.logVersion()

      this.checkAdsFinished()
    })
  }

  /**
   * this function makes a request to the backend to receive the channel content based on the ad type and whether there is an adBlocker present (backend decides over the layout and so on...)
   * @returns an array with the channel data given from the server
   */
  async getContentFromServer () {
    const urlContent = `/rest/contents/homepage?adType=${this.selectedAd}&hpSite=${store.state.request.queryParameters?.hpSite}&adBlocker=${this.isAdBlocker}`
    let hostname = `https://${window.location.hostname}`

    if (window.location.hostname.indexOf('local') > -1) {
      hostname = `https://qa.${window.SiteInfo.brandShort}.${window.SiteInfo.tld}`
    }

    if (window.PageConfig.localhost) {
      hostname = `http://${window.location.host}`
    }

    const url = hostname + urlContent + window.location.search.replace('?', '&')

    const options = {
      method: 'GET',
      headers: store.state.request?.headers
    }

    try {
      const contentTS = new Date().getTime()
      const timeout = Helper.getMonitoringTimer(window.PageConfig.monitoring?.contentServerTimeout)
      const result = await fetch(url, options)
      timeout && clearTimeout(timeout)
      Logger.log(`%cMonitoring: Content Duration: ${new Date().getTime() - contentTS}ms `, { urlParam: 'LogTimeout' })

      const jsonResult = await result.json()
      jsonResult.PageConfig && (window.PageConfig = { ...window.PageConfig, ...jsonResult.PageConfig })
      jsonResult.inactivityDialog && store.commit('setContentData', { inactivityDialog: jsonResult.inactivityDialog })
      store.commit('setAdLayout', jsonResult.adLayout)

      return jsonResult.channels
    } catch (e) {
      store.commit('setAdLayout', 1)

      return []
    }
  }

  /**
   * asks the adservice for the prio ad
   */
  async makePrioAd () {
    await this.initializeAdService()
    const auctionTS = new Date().getTime()
    const auctionTimeout = Helper.getMonitoringTimer(window.PageConfig.monitoring?.adServerTimeout)

    AdServiceHelper.listenEvent('auction.end', () => {
      auctionTimeout && clearTimeout(auctionTimeout)
      Logger.log(`%cMonitoring: Auction Duration: ${new Date().getTime() - auctionTS}ms `, { urlParam: 'LogTimeout' })

      const prioAdTS = new Date().getTime()
      const timeout = Helper.getMonitoringTimer(window.PageConfig.monitoring?.adServerTimeout)
      window.AdService.setSlotParam('prioad', 'maxwidth', 1020)
      window.AdService.singleRequestAdition({
        slots: {
          prioad: (response) => {
            timeout && clearTimeout(timeout)
            Logger.log(`%cMonitoring: Prio Ad Duration: ${new Date().getTime() - prioAdTS}ms `, { urlParam: 'LogTimeout' })

            this.prioAdAvailable(response)
          }
        }
      })
    })
  }

  /**
   * called after we got an ad from adservice
   * if the ad is an event ad this function parses the response
   * if the ad type is billboard the sky right gets called -> the adservice decides if the sky is added to the site
   * @param {object} response
   */
  async prioAdAvailable (response) {
    switch (response.adsize) {
      // lounge ad has 3x3 ad size to make it identifiable but it's still an event like ad which needs parsing
      case '1x1':
      case '3x3': {
        const regex = /\/\*#create#\*\/\[((?:.|[\r\n])+)\]\/\*#etaerc#\*\//
        const adCode = regex.exec(response.ad)

        if (adCode?.length) {
          const eventAd = JSON.parse(adCode[1])
          store.commit('setAdData', eventAd)

          eventAd.color && document.querySelector('body').setAttribute('data-lounge-color', eventAd.color)
          this.selectedAd = eventAd.type

          this.setAdContainers()
        }

        break
      }
      default:
        store.commit('setAdData', response)
        this.selectedAd = response.type

        this.setAdContainers()
    }

    if (this.selectedAd === undefined) {
      // change adType to billboard and not add the container to the DOM, so it's "invisible"
      this.selectedAd = 'billboard'
      this.showMiddle = false
    }

    let channelContent = await this.getContentFromServer()

    switch (this.selectedAd) {
      case 'maxiad': {
        // maxiad has no content what so ever

        const removeBG = () => {
          document.querySelector('[data-ac="middle"]').style.setProperty('background-color', '#fff')
          document.removeEventListener('colCountChanged', removeBG)
        }

        document.addEventListener('colCountChanged', removeBG)

        store.commit('setChannelsRendered', true)
        store.commit('setPageReady', true)
        break
      }
      case 'lounge': {
        if (store.state.adData.params) {
          for (const [key, value] of Object.entries(store.state.adData.params)) {
            window.AdService.setParam(key, value)
          }
        }

        channelContent = CONSTRAINTS.channels.lounge

        // also we have to limit the page to exactly 3 cols for the lounge ad
        const setLoungeCols = () => {
          document.removeEventListener('colCountChanged', setLoungeCols)

          this.contentWidthHelper.minCols = 3
          this.contentWidthHelper.maxCols = 3
          this.contentWidthHelper.setColCase()
        }
        document.addEventListener('colCountChanged', setLoungeCols)

        break
      }
      case 'billboard': {
        const getSkyRight = () => {
          // check if we can fit a sky right
          if (this.contentWidthHelper.checkSkyFits()) {
            this.showRight = true
            this.sticky = true

            const createSky = () => {
              // Wait for sky being rendered
              window.AdService.on('ad.finished', (event) => {
                if (event?.data?.slot !== 'right' || this.contentWidthHelper.skyRightWidth) {
                  return
                }

                // get sky width and add it to contentWidthHelper
                this.contentWidthHelper.skyRightWidth = +event?.slot?._metaData[0]?.width || 0
              })

              window.AdService.setSlotParam('right', 'maxwidth', this.contentWidthHelper.getMaxSkyWidth())
              AdServiceWrapper.makeAd('right')
              document.querySelector('[data-container="right"]').setAttribute('data-no-bg', '')
              document.querySelector('[data-ac="right"]').setAttribute('data-no-bg', '')

              document.removeEventListener('DOMChanged', createSky)
            }

            document.addEventListener('DOMChanged', createSky)
          }
        }

        store.watch((state) => state.pageVisible, getSkyRight)
        break
      }
      default:
        break
    }

    const setPrioAdAttribute = () => {
      document.querySelector('[data-ac][data-service-slot]')?.setAttribute('data-prio-slot', true)
      document.body.setAttribute('data-ad-type', this.selectedAd)
      document.removeEventListener('DOMChanged', setPrioAdAttribute)

      this.setAdContainerProperties()
      this.onAdTypeAvailable()
    }

    document.addEventListener('DOMChanged', setPrioAdAttribute)

    store.commit('setChannels', channelContent)
    Helper.fireEvent('channelsAvailable')
  }

  /**
   * based on the adservice response the ads are being added to the site
   * @param {object} response the adservice response object containing the ad code
   */
  makeEventAds (response) {
    const parts = [this.topName, this.middleName, this.leftName, this.rightName, this.wallName]

    for (const part of parts) {
      const responsePart = response[part]
      let iframeCSS = true

      if (responsePart) {
        let fillMe = ''
        let fallbackWidth = 0
        let fallbackHeight = 0

        // middle fallback is always billboard
        if (part === this.middleName) {
          fallbackWidth = 970
          fallbackHeight = 250
        }

        if (AdServiceWrapper.fallback) {
          this.fallbackWidth = true

          if (responsePart.fallbackWidth) {
            fallbackWidth = responsePart.fallbackWidth
          }

          if (responsePart.fallbackHeight) {
            fallbackHeight = responsePart.fallbackHeight
          }

          if ((part === this.topName || part === this.middleName) && typeof responsePart.fallback === 'object' && responsePart.fallback !== null) {
            fillMe = responsePart.fallback?.['column' + this.colCount + 'Ad']

            const takeSmaller = (currentCols) => {
              const uimTag = AdServiceWrapper.getUIMTag(part, fallbackWidth, fallbackHeight)
              let takeMe = responsePart.fallback['column' + currentCols + 'Ad']

              if (!takeMe) {
                takeMe = responsePart.fallback['column' + (currentCols - 1) + 'Ad']
              }

              return uimTag + takeMe
            }

            document.addEventListener(
              'colCountChanged',
              (colChangeEvent) => {
                const colCase = colChangeEvent.detail.colCount
                const takeMe = takeSmaller(colCase)
                AdServiceWrapper.queueHtml(part, uimTag + takeMe, true)
              },
              false
            )

            fillMe = takeSmaller(this.contentWidthHelper.colCount)
          } else if (part === this.wallName) {
            // we have no fallback ad for the wall position
            continue
          } else {
            fillMe = responsePart.fallback
          }
        } else {
          if (part === this.middleName) {
            iframeCSS = this.setAdSizeVariables(response, this.middleName)
          }

          if (response.type === 'cinematic-wall' && part === this.wallName) {
            AdServiceHelper.listenEvent('ad.wallExpand', () => {
              document.body.setAttribute('data-wall-overlay', '')
            })

            AdServiceHelper.listenEvent('ad.wallContract', () => {
              document.body.removeAttribute('data-wall-overlay')
            })
          } else if (response.type === 'cinematic-wall') {
            // don't load anything other than the wall ad
            continue
          }

          if ((['bridge', 'flagship', 'cinematic'].indexOf(response.type) > -1 || response.variant === 'dynamic') && part === this.middleName) {
            this.dynamicMiddle = true
          }

          if (response.skyVariant !== undefined && (response.skyVariant.length === 0 || response.skyVariant === 'dynamic')) {
            this.dynamicSides = true
          }

          fillMe = responsePart.ad
        }

        const uimTag = AdServiceWrapper.getUIMTag(part, fallbackWidth, fallbackHeight)
        AdServiceWrapper.queueHtml(part, uimTag + fillMe, false, part, iframeCSS)
      }
    }
  }

  /**
   * creates the fallback ads for event ads if an adBlocker has been detected
   */
  makeEventAdblockerFallbackAds () {
    const topPosition = ['wallpaper', 'homeframe', 'cinematic-wall'].indexOf(this.selectedAd) > -1
    const middlePosition = ['billboard', 'flagship', 'bridge', 'cinematic'].indexOf(this.selectedAd) > -1

    // In adblocker case we have no wall and top position
    this.showWall = false
    this.showTop = false

    // Removes unnecessary ad containers to prevent false display
    !window.logout && document.querySelector(`[data-container="${this.wallName}"]`)?.remove()
    !window.logout && document.querySelector(`[data-ac="${this.topName}"]`)?.remove()

    if (topPosition) {
      this.contentWidthHelper.minCols = 2 // 2 cols min cause we now have just a sitebar
      this.contentWidthHelper.superbannerSitebarMinWidth = this.contentWidthHelper.sitebarWidth // overwriting old width value with sitebar value
      this.contentWidthHelper.setOutsideAdSize() // call function to make the new width values active
      this.contentWidthHelper.setColCase() // trigger for possible new colCase
    }

    if (middlePosition) {
      this.contentWidthHelper.minCols = 3 // 3 cols minimum because be only have a billboard in adblock case
      this.contentWidthHelper.setColCase() // trigger for possible new colCase

      // fixes "jumping" billboard
      const middleServiceSlot = document.querySelector(`[data-service-slot="${this.middleName}"]`)

      if (middleServiceSlot) {
        middleServiceSlot.style.opacity = '0'

        AdServiceHelper.listenEvent('ad.useiframecss.done', () => {
          middleServiceSlot.style.opacity = null
        })
      }
    }

    if (['homeframe', 'cinematic-wall', 'double-sitebar'].indexOf(this.selectedAd) > -1) {
      this.showLeft = false
      this.selectedAd = 'sitebar' // so we get the right CSS from _grid.scss otherwise there would be a 120px blank space on the left side

      // Removes unnecessary ad containers to prevent false display
      !window.logout && document.querySelector(`[data-container="${this.leftName}"]`)?.remove()
    }

    if (['bridge', 'flagship'].indexOf(this.selectedAd) > -1) {
      this.showLeft = false
      this.showRight = false
      this.selectedAd = 'billboard' // show only middle fallback for multi-part event ads

      // Removes unnecessary ad containers to prevent false display
      !window.logout && document.querySelector(`[data-container="${this.leftName}"]`)?.remove()
      !window.logout && document.querySelector(`[data-ac="${this.rightName}"]`)?.remove()
    }

    this.showLeft && AdServiceWrapper.makeAd(this.leftName, true)
    this.showMiddle && AdServiceWrapper.makeAd(this.middleName, true)
    this.showRight && AdServiceWrapper.makeAd(this.rightName, true)

    document.querySelector('#acColor')?.remove() // removes the style container given by the BE so we are back to our default colors
    store.commit('setAdData', { ...store.state.adData, bg: null })
    this.setAdContainerProperties()
  }

  /**
   * creates none event ads, so called rotation ads
   * makeAd asks for a new ad
   * queueHtml used existing ad code to be added to the page
   * @param {object} response
   * @param {boolean} isTrackingBlocker
   */
  makeRotationAds (response, isTrackingBlocker) {
    const parts = {
      middle: this.selectedAd === 'billboard' || this.selectedAd === 'maxiad',
      right: this.selectedAd === 'sitebar'
    }

    Object.keys(parts)
      .filter((part) => parts[part])
      .forEach((part) => {
        let iframeCSS = true

        if (part === 'middle') {
          iframeCSS = this.setAdSizeVariables(response, this.middleName)
        }

        if (isTrackingBlocker && !window.logout) {
          AdServiceWrapper.makeAd(part, iframeCSS)
        } else {
          AdServiceWrapper.queueHtml(part, response.ad, false, response.type, iframeCSS)
        }
      })
  }

  /**
   * set ad size css variables from response adsize
   */
  setAdSizeVariables (response, acName) {
    const [width, height] = response?.adsize?.split('x') || []

    if (width && height) {
      document.querySelector(`[data-ac="${acName}"]`).style.setProperty('height', height + 'px')
      document.querySelector(`[data-ac="${acName}"]`).style.setProperty('width', width + 'px')

      return false
    }

    return true
  }

  /**
   * is called if there is no adBlocker detected to create the ads based on the tracking detection
   * @param {boolean} isTrackingBlocker
   */
  callAfterTrackingBlockerDetected (isTrackingBlocker) {
    this.fallback = AdServiceWrapper.showFallback()
    const { adData } = store.state

    if (adData.top || adData.middle || adData.left || adData.right) {
      if (isTrackingBlocker && adData.blockClean) {
        this.makeEventAdblockerFallbackAds()
      } else {
        this.makeEventAds(adData, isTrackingBlocker)
      }
    } else {
      this.makeRotationAds(adData, isTrackingBlocker)
    }

    this.makeContentAds()
  }

  /**
   * based on the param value either the fallback ads are being created or the tracking detection gets started
   * @param {boolean} isAdBlocker
   */
  async callAfterAdBlockerDetection (isAdBlocker) {
    this.isAdBlocker = isAdBlocker

    if (!isAdBlocker) {
      this.setAdContainerProperties()

      const isTrackingBlocker = AdServiceWrapper.checkTrackingBlocker()
      this.callAfterTrackingBlockerDetected(isTrackingBlocker)
    } else {
      this.fallback = true
      this.makeEventAdblockerFallbackAds()
      this.makeContentAds()
    }
  }

  /**
   * Set click url for ad container and bg color
   */
  setAdContainerProperties () {
    if (store.state.adData.bg) {
      const { clickurl, color } = store.state.adData.bg;

      (clickurl || color) &&
      document.querySelectorAll('[data-ac], [data-container]').forEach((element) => {
        color && element.style.setProperty('background-color', color)

        if (element) {
          element.style.cursor = 'pointer'
          element.dataset.url = clickurl
          element.addEventListener('click', (event) => {
            event.target?.dataset?.url && window.open(event.target.dataset.url, '_blank')
          })
        }
      })
      return
    }
    // reset all
    document.querySelectorAll('[data-ac], [data-container]').forEach((element) => {
      element.style.cursor = 'auto'
      element.dataset.url = ''
    })
  }

  /**
   * the ads inside the content get created
   */
  makeContentAds () {
    if (!this.isAdBlocker) {
      AdServiceWrapper.makeAd('notification')
      AdServiceWrapper.makePartnerlinks()
      AdServiceWrapper.makeFooterLinklist()
    }

    if (!window.logout) {
      AdServiceWrapper.makeBoxAds()
      AdServiceWrapper.makeRecoBoxAds()
    }

    AdServiceWrapper.makeVorteilswelt()
    AdServiceWrapper.makeAd('tam')
  }

  /**
   * Set body attribute for automated testing after all ads are loaded
   * Change recobox attribute if version '_big' is returned and remove the 2nd one
   */
  checkAdsFinished () {
    const setAutomatedTrackingAttr = !!this.urlParams.get('showComplete')

    let timeoutHandle
    const setTimer = () => {
      timeoutHandle = setTimeout(() => {
        document.body.setAttribute('data-adservice-complete', 'true')
      }, 5000)
    }
    setAutomatedTrackingAttr && setTimer()

    window.AdService.on('ad.finished', (event) => {
      if (setAutomatedTrackingAttr) {
        clearTimeout(timeoutHandle)
        setTimer()
      }

      if (event.data.slot === 'recobox_1' && event.slot._metaData[0].adsize === '630x250') {
        document.querySelector('[data-service-slot="recobox_2"]').remove()

        const recoboxOne = document.querySelector('[data-service-slot="recobox_1"]')
        recoboxOne.setAttribute('data-service-slot', 'recobox_big')
        recoboxOne.hasAttribute('data-tr-component-path') && recoboxOne.setAttribute('data-tr-component-path', 'recobox_big')

        recoboxOne.querySelector('iframe').style.width = '630px'
        recoboxOne.querySelector('iframe').style.height = '250px'
      }
    })
  }
}
